import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BpCoreModule } from '@bp-core/src/public-api';
import { AdvisorNotificationsStore } from 'bp/shared/services/advisor-notifications.store';

import { BpNotificationPanelComponent } from './bp-notification-panel.component';

/** A partir de 100 no leídas el contador deja de ser un número exacto (el servidor recorta ahí). */
const BADGE_MAX = 99;

/**
 * Campana de notificaciones del encabezado del asesor.
 *
 * Vive en el layout, fuera del slot `action` del encabezado (que es excluyente), así que está
 * presente en todas las pantallas. Arranca el sondeo del contador UNA vez — el store es único —
 * y despliega el panel con CDK Overlay: `Escape` y el clic afuera lo cierran, y el botón es un
 * botón nativo (`mat-icon-button`), de modo que `Enter` y `Espacio` ya lo abren sin código extra.
 */
@Component({
  selector: 'bp-notification-bell',
  standalone: true,
  // `Overlay` (servicio) y `TemplatePortal` (clase) se usan desde TypeScript, no desde el
  // template: sus NgModules no hacen falta en `imports` y solo sumaban peso al bundle.
  imports: [MatButtonModule, MatIconModule, BpCoreModule, BpNotificationPanelComponent],
  templateUrl: './bp-notification-bell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BpNotificationBellComponent implements OnInit, OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(AdvisorNotificationsStore);

  // Opcionales: sin rol de campana el botón no se renderiza (feature 059).
  private readonly origin = viewChild('bellOrigin', { read: ElementRef });
  private readonly panelTemplate = viewChild<TemplateRef<unknown>>('panelTemplate');

  readonly isOpen = signal(false);

  /**
   * Feature 059 (FR-018, AS-35): solo con un rol de campana (`NOTIFICATION_BELL_ROLES`). Un
   * `crm-viewer` solo no la ve. El store tampoco sondea sin rol.
   */
  readonly isVisible = this.store.hasBellAccess;

  /** `null` mientras no se sabe: se muestra el esqueleto, nunca un "0" provisorio. */
  readonly isLoading = this.store.isLoading;
  readonly unreadCount = this.store.unreadCount;

  readonly hasBadge = computed(() => (this.unreadCount() ?? 0) > 0);
  readonly badgeLabel = computed(() => {
    const count = this.unreadCount() ?? 0;
    return count > BADGE_MAX ? `${BADGE_MAX}+` : `${count}`;
  });

  private overlayRef: OverlayRef | null = null;

  ngOnInit(): void {
    this.store.start();
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  togglePanel(): void {
    if (this.isOpen()) {
      this.closePanel();
      return;
    }
    this.openPanel();
  }

  closePanel(): void {
    this.overlayRef?.detach();
    this.isOpen.set(false);
  }

  private openPanel(): void {
    const origin = this.origin();
    const panelTemplate = this.panelTemplate();
    if (!origin || !panelTemplate) {
      return;
    }
    const overlayRef = this.overlayRef ?? this.createOverlay(origin);
    overlayRef.attach(new TemplatePortal(panelTemplate, this.viewContainerRef));
    this.isOpen.set(true);
  }

  private createOverlay(origin: ElementRef): OverlayRef {
    const overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(origin.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
        ]),
    });

    overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closePanel());

    overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closePanel();
        }
      });

    this.overlayRef = overlayRef;
    return overlayRef;
  }
}
